/* eslint-disable */
// Invite-a-Dep dialog (iter-126d, slice 5 of EventDetail.jsx refactor).
//
// Two tabs:
//   1. Existing Dep — single PlayerPicker OR bulk-invite checkbox grid,
//      with section/replacing-player selects + optional message.
//   2. Invite Someone New — register a new dep on the spot (name, email,
//      phone, message) and email them the event request.
//
// All state is owned by the parent; this is a pure-presentation component.
import React from "react";
import { Send, UserPlus } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "../../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import PlayerPicker from "../../components/PlayerPicker";


export const InviteDepDialog = ({
  open,
  onOpenChange,
  availableDeps,
  deps,
  sections,
  memberAttendance,
  depInviteData,
  setDepInviteData,
  newDepData,
  setNewDepData,
  submittingNewDep,
  handleInviteDep,
  handleInviteNewDep,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="glass border-white/10" data-testid="invite-dep-dialog">
      <DialogHeader>
        <DialogTitle className="font-heading text-xl">Invite a Dep</DialogTitle>
      </DialogHeader>
      <Tabs defaultValue={availableDeps.length > 0 ? "existing" : "new"} className="mt-4">
        <TabsList className="grid w-full grid-cols-2 bg-secondary/40">
          <TabsTrigger value="existing" data-testid="dep-tab-existing">Existing Dep</TabsTrigger>
          <TabsTrigger value="new" data-testid="dep-tab-new">Invite Someone New</TabsTrigger>
        </TabsList>

        {/* ─── Existing dep ─── */}
        <TabsContent value="existing" className="space-y-4 pt-4">
          {availableDeps.length > 0 ? (
            <>
              {/* iter-91: bulk-invite toggle. Off = single-select via
                  PlayerPicker. On = checkbox grid; tick as many as needed. */}
              <div className="flex items-center justify-between">
                <Label>Select Dep(s) *</Label>
                <div className="flex items-center gap-2">
                  <Label htmlFor="bulk-toggle" className="text-xs text-muted-foreground cursor-pointer">
                    Bulk invite
                  </Label>
                  <Switch
                    id="bulk-toggle"
                    checked={!!depInviteData.bulk}
                    onCheckedChange={(checked) =>
                      setDepInviteData({
                        ...depInviteData,
                        bulk: checked,
                        dep_player_id: checked ? "" : depInviteData.dep_player_id,
                        dep_player_ids: checked ? (depInviteData.dep_player_ids || []) : [],
                      })
                    }
                    data-testid="bulk-invite-toggle"
                  />
                </div>
              </div>

              <div className="space-y-2">
                {depInviteData.bulk ? (
                  <div
                    className="max-h-64 overflow-y-auto rounded-lg border border-white/[0.06] bg-secondary/30 divide-y divide-white/[0.04]"
                    data-testid="bulk-invite-list"
                  >
                    {availableDeps.map((d) => {
                      const selected = (depInviteData.dep_player_ids || []).includes(d.id);
                      return (
                        <label
                          key={d.id}
                          htmlFor={`bulk-dep-${d.id}`}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-white/[0.03]"
                        >
                          <input
                            id={`bulk-dep-${d.id}`}
                            type="checkbox"
                            checked={selected}
                            onChange={(e) => {
                              const current = depInviteData.dep_player_ids || [];
                              const next = e.target.checked
                                ? [...current, d.id]
                                : current.filter((x) => x !== d.id);
                              // iter-107d: clear stale section when the
                              // selected dep set changes — the old
                              // section may no longer be common.
                              setDepInviteData({
                                ...depInviteData,
                                dep_player_ids: next,
                                section_id: "",
                              });
                            }}
                            data-testid={`bulk-dep-checkbox-${d.id}`}
                            className="w-4 h-4 accent-primary"
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{d.name}</p>
                            {d.email && (
                              <p className="text-xs text-muted-foreground truncate">{d.email}</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <PlayerPicker
                    value={depInviteData.dep_player_id || null}
                    players={availableDeps}
                    sections={sections}
                    onChange={(id) => setDepInviteData({
                      ...depInviteData,
                      dep_player_id: id || "",
                      // iter-107d: clear section so the dropdown
                      // resets and only shows the new dep's sections.
                      section_id: "",
                    })}
                    placeholder="Select a dep..."
                    testId="invite-dep-picker"
                  />
                )}
                {depInviteData.bulk && (
                  <p className="text-xs text-muted-foreground">
                    {(depInviteData.dep_player_ids || []).length} selected
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Replacing which player? (optional)</Label>
                <Select
                  value={depInviteData.for_player_id || "none"}
                  onValueChange={(value) => setDepInviteData({ ...depInviteData, for_player_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger className="bg-secondary/50" data-testid="dep-for-player-select">
                    <SelectValue placeholder="Defaults to you" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Me (default)</SelectItem>
                    {memberAttendance
                      .slice()
                      .sort((a, b) => (a.player_name || "").localeCompare(b.player_name || ""))
                      .map((m) => (
                        <SelectItem key={m.player_id} value={m.player_id}>
                          {m.player_name}
                          {m.part_name ? ` · ${m.part_name}` : ""}
                          {m.status === "not_attending" ? " (Can't go)" : m.status === "maybe" ? " (Maybe)" : ""}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-muted-foreground">
                  Pick the player whose seat this dep is filling. Used by the Missing Players report.
                </p>
              </div>

              <div className="space-y-2">
                <Label>
                  For Section <span className="text-red-400">*</span>
                </Label>
                {(() => {
                  // iter-107d: Restrict the section dropdown to the
                  // intersection of every selected dep's own sections.
                  // Bulk mode = intersection across selected deps; single
                  // mode = that one dep's sections. Empty intersection
                  // surfaces a helpful message instead of a blank menu.
                  const selectedIds = depInviteData.dep_player_ids?.length
                    ? depInviteData.dep_player_ids
                    : (depInviteData.dep_player_id ? [depInviteData.dep_player_id] : []);
                  const depsByIdInDialog = new Map((deps || []).map((d) => [d.id, d]));
                  const selectedDeps = selectedIds
                    .map((id) => depsByIdInDialog.get(id))
                    .filter(Boolean);
                  let commonSectionIds = [];
                  if (selectedDeps.length > 0) {
                    commonSectionIds = (selectedDeps[0].section_ids || []).filter(
                      (sid) => selectedDeps.every((d) =>
                        (d.section_ids || []).includes(sid)
                      )
                    );
                  }
                  const availableSections = sections.filter((s) =>
                    commonSectionIds.includes(s.id)
                  );
                  const noDepSelected = selectedDeps.length === 0;
                  return (
                    <>
                      <Select
                        value={depInviteData.section_id || ""}
                        onValueChange={(value) =>
                          setDepInviteData({ ...depInviteData, section_id: value })
                        }
                        disabled={noDepSelected || availableSections.length === 0}
                      >
                        <SelectTrigger className="bg-secondary/50" data-testid="dep-section-select">
                          <SelectValue placeholder="Please select" />
                        </SelectTrigger>
                        <SelectContent>
                          {availableSections.map((section) => (
                            <SelectItem key={section.id} value={section.id}>
                              {section.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {noDepSelected && (
                        <p className="text-[11px] text-muted-foreground">
                          Pick a dep first — sections will appear here.
                        </p>
                      )}
                      {!noDepSelected && availableSections.length === 0 && (
                        <p className="text-[11px] text-amber-400">
                          The selected deps share no sections. Pick deps that play in the same section to bulk-invite.
                        </p>
                      )}
                      {!noDepSelected && availableSections.length > 0 && (
                        <p className="text-[11px] text-muted-foreground">
                          Only sections the selected dep
                          {selectedDeps.length === 1 ? " plays" : "s all play"} in are shown.
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>

              <div className="space-y-2">
                <Label>Message (optional)</Label>
                <Textarea
                  placeholder="Add a personal message..."
                  value={depInviteData.message}
                  onChange={(e) => setDepInviteData({ ...depInviteData, message: e.target.value })}
                  className="bg-secondary/50"
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                <Button
                  className="bg-primary text-primary-foreground"
                  onClick={handleInviteDep}
                  data-testid="confirm-invite-dep-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Send Invitation
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <UserPlus className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No deps available to invite from your section.</p>
              <p className="text-sm mt-2">Use <em>Invite Someone New</em> to add one on the spot.</p>
            </div>
          )}
        </TabsContent>

        {/* ─── Invite someone new ─── */}
        <TabsContent value="new" className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground">
            Adds them to your band as a deputy musician and emails them the event request with sign-in details.
          </p>
          <div className="space-y-2">
            <Label>Name *</Label>
            <Input
              data-testid="new-dep-name-input"
              placeholder="Sam Jones"
              value={newDepData.name}
              onChange={(e) => setNewDepData({ ...newDepData, name: e.target.value })}
              className="bg-secondary/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Email *</Label>
            <Input
              data-testid="new-dep-email-input"
              type="email"
              placeholder="sam@example.com"
              value={newDepData.email}
              onChange={(e) => setNewDepData({ ...newDepData, email: e.target.value })}
              className="bg-secondary/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Phone (optional)</Label>
            <Input
              data-testid="new-dep-phone-input"
              type="tel"
              placeholder="07123 456789"
              value={newDepData.phone}
              onChange={(e) => setNewDepData({ ...newDepData, phone: e.target.value })}
              className="bg-secondary/50"
            />
          </div>
          <div className="space-y-2">
            <Label>Personal message (optional)</Label>
            <Textarea
              data-testid="new-dep-message-input"
              placeholder="Any chance you could cover this one for me?"
              value={newDepData.message}
              onChange={(e) => setNewDepData({ ...newDepData, message: e.target.value })}
              className="bg-secondary/50"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            They&apos;ll be added to your primary section. They&apos;ll only see this event — no other band data.
          </p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button
              className="bg-primary text-primary-foreground"
              onClick={handleInviteNewDep}
              disabled={submittingNewDep}
              data-testid="confirm-invite-new-dep-btn"
            >
              <Send className="w-4 h-4 mr-2" />
              {submittingNewDep ? "Sending..." : "Register & Invite"}
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </DialogContent>
  </Dialog>
);

export default InviteDepDialog;
